import axios from "axios";

const API_BASE_URL = "/api";

const authApi = axios.create({
  baseURL: API_BASE_URL,
  headers: { "Content-Type": "application/json" },
  withCredentials: true, // send HttpOnly cookies with every request
});

function notifyAuthUserChanged() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event("auth_user_changed"));
  }
}

// ============================================
// SELF-SIGNUP
// ============================================
export const signupOrg = async (orgName: string, name: string, mobile: string, password: string, businessType?: string) => {
  try {
    const response = await authApi.post("/auth/signup", { orgName, name, mobile, password, ...(businessType ? { businessType } : {}) });
    if (response.data.user) {
      localStorage.setItem("auth_user", JSON.stringify(response.data.user));
      notifyAuthUserChanged();
    }
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || "Signup failed");
  }
};

// ============================================
// LOGIN (two-step)
// ============================================

// Step 1: verify credentials, get list of tenants this mobile belongs to.
// If the user is a platform-level SYSTEM_OWNER, returns { isSuperAdmin: true } — no tenant selection needed.
export const getLoginTenants = async (mobile: string, password: string) => {
  try {
    const response = await authApi.post("/auth/login/tenants", { mobile, password });
    return response.data as
      | { isSuperAdmin: true }
      | { tenants: { tenantId: string; name: string; logoUrl: string | null }[] };
  } catch (error: any) {
    throw new Error(error.response?.data?.message || "Login failed");
  }
};

// Step 2: confirm tenant selection, receive JWT.
// SYSTEM_OWNER logs in without a tenantId (platform-level, no org affiliation).
export const loginUser = async (mobile: string, password: string, tenantId?: string) => {
  try {
    const response = await authApi.post("/auth/login", { mobile, password, ...(tenantId ? { tenantId } : {}) });

    // Tokens (auth_token, refresh_token) are set as HttpOnly cookies by the server.
    // Only store non-sensitive user profile data for UI use.
    if (response.data.user) {
      localStorage.setItem("auth_user", JSON.stringify(response.data.user));
      notifyAuthUserChanged();
    }

    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || "Login failed");
  }
};

// ============================================
// LOGOUT
// ============================================

export const logoutUser = async () => {
  try {
    // withCredentials sends HttpOnly cookies automatically; the server blacklists
    // both tokens and clears the cookies with Set-Cookie in its response.
    const response = await authApi.post("/auth/logout");

    if (response.status === 200) {
      localStorage.removeItem("auth_user");

      if (typeof window !== "undefined") {
        window.location.href = "/";
      }
    }
  } catch (error) {
    console.warn("Logout API failed:", error);

    // Clear local state even if the API call failed. The HttpOnly cookies
    // cannot be cleared from JavaScript — they will expire naturally or be
    // rejected by the server (blacklisted) once connectivity is restored.
    localStorage.removeItem("auth_user");

    if (typeof window !== "undefined") {
      // On explicit logout go to landing page; on forced logout (session expired) stay on unauthorized
      window.location.href = "/";
    }
  }
};

// ============================================
// AUTH HELPERS
// ============================================

// isAuthenticated checks for a stored user profile rather than reading the
// HttpOnly cookie (which is intentionally inaccessible to JavaScript).
export const getToken = () => null; // tokens are HttpOnly — not readable from JS

export const isAuthenticated = () => !!getUser();

export const getUser = () => {
  if (typeof window === "undefined") return null;
  const user = localStorage.getItem("auth_user");
  return user ? JSON.parse(user) : null;
};

// Fetches the current user's profile from the server (identity derived from
// the HttpOnly JWT cookie, not from any client-controlled input). Call this on
// app startup so that any tampered localStorage auth_user is immediately
// overwritten with the authoritative server value.
export const refreshUserFromServer = async (): Promise<void> => {
  try {
    const response = await authApi.get("/auth/me");
    if (response.data) {
      localStorage.setItem("auth_user", JSON.stringify(response.data));
      notifyAuthUserChanged();
    }
  } catch {
    // 401 means the session has expired — clear stale state and let the app
    // redirect to login naturally (the route guard in middleware.ts handles it).
    localStorage.removeItem("auth_user");
  }
};

// ============================================
// REFRESH TOKEN INTERCEPTOR
// ============================================

let isRefreshing = false;
let failedQueue: Array<{
  onSuccess: () => void;
  onFailure: (error: any) => void;
}> = [];

const processQueue = (error: any) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.onFailure(error);
    } else {
      prom.onSuccess();
    }
  });
  failedQueue = [];
};

export const setupAxiosInterceptors = () => {
  // No-op request interceptor: tokens are HttpOnly cookies sent automatically
  // by the browser via withCredentials — no manual header attachment needed.

  // Handle 401 and silently refresh using the HttpOnly refresh_token cookie
  authApi.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      if (error.response?.status === 401 && !originalRequest._retry) {
        // Never try to refresh on login/refresh endpoints — let the error propagate
        // so the UI (LoginModal) can display it.
        const url: string = originalRequest.url || "";
        if (url.includes("/auth/login") || url.includes("/auth/refresh")) {
          return Promise.reject(error);
        }

        if (isRefreshing) {
          return new Promise<void>((onSuccess, onFailure) => {
            failedQueue.push({ onSuccess, onFailure });
          })
            .then(() => authApi(originalRequest))
            .catch((err) => Promise.reject(err));
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          // refresh_token is an HttpOnly cookie — sent automatically
          await authApi.post("/auth/refresh");
          // Server set new auth_token + refresh_token cookies; retry original request
          processQueue(null);
          return authApi(originalRequest);
        } catch (err) {
          processQueue(err);
          logoutUser();
          return Promise.reject(err);
        } finally {
          isRefreshing = false;
        }
      }

      return Promise.reject(error);
    },
  );
};

// Initialise interceptors at module load so every import of this file
// automatically gets the Bearer token attached and refresh-rotation handled.
setupAxiosInterceptors();

export default authApi;
